import { apiClient } from './apiClient';
import Config from '../config/config';
import { Logger } from '../utils/logger';
import { GENERATION_HTTP_TIMEOUT_MS } from '../utils/Constants';

export interface GenerationOptions {
  topic?: string;
  count?: number;
  channelId?: string;
  platform?: 'linkedin' | 'twitter' | 'facebook' | 'instagram';
  language?: string;
  useSources?: boolean;
  forceRefreshSources?: boolean;
  signal?: AbortSignal;
}

export interface IngestionStatus {
  running: boolean;
  interval_hours: number;
  jobs: Array<{
    id: string;
    name: string;
    next_run: string | null;
  }>;
}

export interface SyncResult {
  success: boolean;
  message: string;
  synced?: number;
  failed?: number;
  details?: Array<{
    source_id: string;
    success: boolean;
    error?: string;
  }>;
}

export interface ImageModel {
  key: string;
  name: string;
  provider: string;
  description: string;
  available: boolean;
}

export interface ImageGenerationResult {
  url: string;
  object_name: string;
  keywords: string;
  style: string;
  platform: string;
  model: string;
  model_name: string;
  width: number;
  height: number;
}

/**
 * Snapshot terminal d'une session de génération, persisté côté backend (Redis TTL).
 * Permet de RÉCUPÉRER le résultat quand l'event temps-réel WS a été perdu
 * (relais déconnecté pendant la génération — Redis pub/sub sans replay).
 */
export interface GenerationResultSnapshot {
  type: 'complete' | 'error';
  data: {
    posts?: unknown[];
    count?: number;
    error?: string;
    i18n_key?: string;
    i18n_params?: Record<string, unknown>;
  };
}

export const generationService = {
  /**
   * Récupère le résultat terminal d'une session via GET /generation/{id}/result.
   * Retourne null si aucun résultat persisté (404 : jamais lancée, encore en cours,
   * ou expiré) — le caller retombe alors sur son fallback.
   */
  getGenerationResult: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<GenerationResultSnapshot | null> => {
    try {
      const response = await apiClient.get<GenerationResultSnapshot>(
        `/generation/${sessionId}/result`,
        { signal },
      );
      return response.data;
    } catch (e: unknown) {
      // 404 attendu (pas de résultat persisté) → null sans bruit ; autres erreurs logguées.
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        Logger.warn('[GenerationService] getGenerationResult failed', {
          sessionId,
          status: status ?? 'unknown',
        });
      }
      return null;
    }
  },

  generatePosts: async (spaceId: string, options?: GenerationOptions) => {
    Logger.info('[GenerationService] Generating posts', {
      spaceId,
      count: options?.count || 1,
      channelId: options?.channelId ?? null,
      platform: options?.platform || 'linkedin',
      language: options?.language || 'fr',
      useSources: options?.useSources !== false,
      forceRefreshSources: options?.forceRefreshSources || false,
    });
    const payload = {
      space_id: spaceId,
      topic: options?.topic,
      count: options?.count || 1,
      channel_id: options?.channelId,
      platform: options?.platform || 'linkedin',
      language: options?.language || 'fr',
      use_sources: options?.useSources !== false, // Par défaut true
      force_refresh_sources: options?.forceRefreshSources || false
    };
    
    // V4-FRONT-ANY-USAGE batch 6 2026-05-29 — la réponse est une liste de posts
    // bruts (snake_case) ; le caller (useGeneration) la mappe via WsPost. On la
    // type `unknown` ici (pas de DTO partagé service↔hook) et le caller cast.
    // Génération LLM multi-posts : bien plus longue que le défaut axios (30 s).
    const response = await apiClient.post<unknown>('/generation/generate-batch', payload, {
      signal: options?.signal,
      timeout: GENERATION_HTTP_TIMEOUT_MS,
    });
    Logger.info('[GenerationService] Post generation request completed', { spaceId });
    return response.data;
  },

  improvePost: async (content: string, instructions?: string, platform?: string) => {
    Logger.info('[GenerationService] Improving post', {
      contentLength: content.length,
      hasInstructions: !!instructions,
      platform: platform || 'linkedin',
    });
    const response = await apiClient.post<{ content: string; explanation?: string }>('/generation/improve', {
      content,
      instructions,
      platform: platform || 'linkedin'
    });
    Logger.info('[GenerationService] Post improvement completed', { platform: platform || 'linkedin' });
    return response.data;
  },

  // Ingestion API
  getIngestionStatus: async (): Promise<IngestionStatus> => {
    Logger.debug('[GenerationService] Fetching ingestion status');
    const response = await apiClient.get<IngestionStatus>('/ingestion/status');
    Logger.info('[GenerationService] Ingestion status fetched', {
      running: response.data.running,
      jobsCount: response.data.jobs.length,
    });
    return response.data;
  },

  syncSpace: async (spaceId: string): Promise<SyncResult> => {
    Logger.info('[GenerationService] Syncing space sources', { spaceId });
    const response = await apiClient.post<SyncResult>('/ingestion/sync/space', {
      space_id: spaceId
    });
    Logger.info('[GenerationService] Space sync completed', {
      spaceId,
      success: response.data.success,
      synced: response.data.synced ?? 0,
      failed: response.data.failed ?? 0,
    });
    return response.data;
  },

  syncSource: async (sourceId: string): Promise<SyncResult> => {
    Logger.info('[GenerationService] Syncing source', { sourceId });
    const response = await apiClient.post<SyncResult>('/ingestion/sync/source', {
      source_id: sourceId
    });
    Logger.info('[GenerationService] Source sync completed', {
      sourceId,
      success: response.data.success,
      synced: response.data.synced ?? 0,
      failed: response.data.failed ?? 0,
    });
    return response.data;
  },

  invalidateSourceCache: async (sourceId: string): Promise<{ success: boolean; message: string }> => {
    Logger.warn('[GenerationService] Invalidating source cache', { sourceId });
    const response = await apiClient.delete<{ success: boolean; message: string }>(`/ingestion/cache/${sourceId}`);
    Logger.info('[GenerationService] Source cache invalidated', { sourceId, success: response.data.success });
    return response.data;
  },

  // Image Generation API
  getImageStyles: async (): Promise<Array<{ key: string; name: string }>> => {
    Logger.debug('[GenerationService] Fetching image styles');
    const response = await apiClient.get<Array<{ key: string; name: string }>>('/media/image-generation/styles');
    Logger.info('[GenerationService] Image styles fetched', { count: response.data.length });
    return response.data;
  },

  getImageModels: async (): Promise<ImageModel[]> => {
    Logger.debug('[GenerationService] Fetching image models');
    const response = await apiClient.get<ImageModel[]>('/media/image-generation/models');
    Logger.info('[GenerationService] Image models fetched', { count: response.data.length });
    return response.data;
  },

  getImageDimensions: async (): Promise<Record<string, { width: number; height: number; description: string }>> => {
    Logger.debug('[GenerationService] Fetching image dimensions');
    const response = await apiClient.get<Record<string, { width: number; height: number; description: string }>>('/media/image-generation/dimensions');
    Logger.info('[GenerationService] Image dimensions fetched', { count: Object.keys(response.data).length });
    return response.data;
  },

  /**
   * Prévisualise le prompt anglais qui SERAIT envoyé au modèle d'image,
   * sans déclencher la génération (workflow 2-temps — chantier M 2026-05).
   *
   * Le caller peut ensuite afficher ce prompt en textarea éditable, le modifier
   * puis appeler `generateImage()` avec `promptOverride` pour lancer la génération
   * avec le prompt final.
   *
   * Rate-limit backend : 1 preview / 10s / (user, content hash) → 429 si trop fréquent.
   */
  previewImagePrompt: async (
    postContent: string,
    platform: string,
    spaceId?: string,
  ): Promise<{ prompt: string }> => {
    Logger.info('[GenerationService] Preview image prompt', {
      platform,
      spaceId: spaceId ?? null,
    });
    const response = await apiClient.post<{ prompt: string }>(
      '/media/image-generation/preview-prompt',
      {
        post_content: postContent,
        platform,
        space_id: spaceId,
      },
      { timeout: 60000 },
    );
    Logger.info('[GenerationService] Preview prompt OK', { length: response.data.prompt?.length ?? 0 });
    return response.data;
  },

  generateImage: async (
    postContent: string,
    style: string,
    platform: string,
    spaceId?: string,
    model: string = 'openjourney',
    width?: number,
    height?: number,
    promptOverride?: string,
  ): Promise<ImageGenerationResult> => {
    Logger.info('[GenerationService] Generating image', {
      platform,
      style,
      spaceId: spaceId ?? null,
      model,
      width: width ?? null,
      height: height ?? null,
      hasPromptOverride: !!promptOverride,
    });
    const response = await apiClient.post<ImageGenerationResult>('/media/image-generation/generate', {
      post_content: postContent,
      style,
      platform,
      model,
      space_id: spaceId,
      width,
      height,
      prompt_override: promptOverride,
    }, { timeout: GENERATION_HTTP_TIMEOUT_MS });
    
    // Construire l'URL sécurisée via le proxy backend au lieu de l'URL MinIO directe
    const secureUrl = `${Config.getInstance().apiUrl}/media/serve/${response.data.object_name}`;
    Logger.info('[GenerationService] Image generated', {
      objectName: response.data.object_name,
      model: response.data.model,
      platform: response.data.platform,
    });
    
    return {
      ...response.data,
      url: secureUrl  // Remplacer l'URL MinIO par l'URL proxy sécurisée
    };
  },

  /**
   * Itère sur une image déjà générée via une instruction texte libre
   * ("change le fond", "rajeunis la personne"). V4-IMAGE-EDIT.
   * Renvoie une nouvelle image (même shape que generateImage).
   */
  editImage: async (
    objectName: string,
    instruction: string,
    model?: string,
    platform: string = 'linkedin',
    width?: number,
    height?: number,
  ): Promise<ImageGenerationResult> => {
    Logger.info('[GenerationService] Editing image', { objectName, model: model ?? null, platform });
    const response = await apiClient.post<ImageGenerationResult>('/media/image-generation/edit', {
      object_name: objectName,
      instruction,
      model,
      platform,
      width,
      height,
    }, { timeout: GENERATION_HTTP_TIMEOUT_MS });

    const secureUrl = `${Config.getInstance().apiUrl}/media/serve/${response.data.object_name}`;
    Logger.info('[GenerationService] Image edited', {
      objectName: response.data.object_name,
      model: response.data.model,
    });
    return { ...response.data, url: secureUrl };
  },

  /**
   * Construit l'URL sécurisée pour un object_name donné.
   * Utilisé pour reconstruire les URLs des images existantes.
   */
  getSecureMediaUrl: (objectName: string): string => {
    Logger.debug('[GenerationService] Building secure media URL', { objectName });
    return `${Config.getInstance().apiUrl}/media/serve/${objectName}`;
  },

  /**
   * Démarre une génération de post à partir d'un visuel pré-uploadé (image / carrousel / PDF).
   *
   * Le backend déclenche le pipeline multi-agent en mode `from_visual` : le VisualContextAgent
   * remplace le KnowledgeAgent et alimente le WriterAgent avec une analyse vision du visuel.
   *
   * La progression est publiée sur le channel Redis `generation:{session_id}` ; le caller doit
   * souscrire au WebSocket `/ws/generation/{session_id}` (cf. useGenerateFromVisual).
   */
  startGenerationFromVisual: async (params: {
    sessionId: string;
    spaceId: string;
    channelId?: string;
    platform?: string;
    language?: string;
    imageUrls: string[];
    visualBrief?: string;
    platformPositioning?: string;
    styleExampleIds?: string[];
    styleExamplesRaw?: string[];
  }): Promise<{ session_id: string; status: string }> => {
    Logger.info('[GenerationService] Starting from-visual generation', {
      sessionId: params.sessionId,
      spaceId: params.spaceId,
      imageCount: params.imageUrls.length,
      hasBrief: !!params.visualBrief,
      platform: params.platform || 'linkedin',
    });
    const response = await apiClient.post<{ session_id: string; status: string }>(
      '/generation/from-visual',
      {
        session_id: params.sessionId,
        space_id: params.spaceId,
        channel_id: params.channelId,
        platform: params.platform || 'linkedin',
        language: params.language || 'fr',
        image_urls: params.imageUrls,
        visual_brief: params.visualBrief,
        platform_positioning: params.platformPositioning,
        style_example_ids: params.styleExampleIds,
        style_examples_raw: params.styleExamplesRaw,
      },
    );
    Logger.info('[GenerationService] From-visual generation triggered', { sessionId: params.sessionId });
    return response.data;
  },
};